/**
 * Not exported from the `@cavela/ui` barrel: importing it pulls in MDXEditor,
 * Lexical and the editor stylesheet, which every other `@cavela/ui` consumer
 * would otherwise load too. Import it from
 * `@cavela/ui/components/ui/MarkdownEditor`.
 */
import { cn } from '@heroui/react';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  INLINE_CODE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK,
  QUOTE,
  UNORDERED_LIST
} from '@lexical/markdown';
import {
  addComposerChild$,
  addNestedEditorChild$,
  addTableCellEditorChild$,
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  headingsPlugin,
  imagePlugin,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  type RealmPlugin,
  realmPlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo
} from '@mdxeditor/editor';

import { escapeOrderedListMarkers } from '../../../utils/markdownUtils';
import { quoteParagraphsPlugin } from './quoteParagraphsPlugin';

import '@mdxeditor/editor/style.css';

/**
 * Same as MDXEditor's markdownShortcutPlugin, but without the ordered-list
 * transformer. Typing `1. ` / `2. ` stays as paragraph text instead of
 * creating (and nesting) numbered lists.
 */
const markdownShortcutWithoutNumberedListsPlugin = realmPlugin({
  init(realm) {
    const transformers = [
      BOLD_ITALIC_STAR,
      BOLD_ITALIC_UNDERSCORE,
      BOLD_STAR,
      BOLD_UNDERSCORE,
      INLINE_CODE,
      ITALIC_STAR,
      ITALIC_UNDERSCORE,
      QUOTE,
      LINK,
      UNORDERED_LIST
    ];
    const Shortcut = () => (
      <MarkdownShortcutPlugin transformers={transformers} />
    );

    realm.pubIn({
      [addComposerChild$]: Shortcut,
      [addNestedEditorChild$]: Shortcut,
      [addTableCellEditorChild$]: Shortcut
    });
  }
});

/** Shared prose classes for MDXEditor content areas. */
export const MARKDOWN_EDITOR_CONTENT_CLASS =
  'font-figtree text-primary-text max-w-none focus:outline-none [&_h1]:block [&_h1]:text-[22px] [&_h1]:font-semibold [&_h1]:tracking-[-0.22px] [&_h2]:block [&_h2]:text-[20px] [&_h2]:font-semibold [&_h2]:tracking-[-0.2px] [&_h2]:mt-2 [&_h3]:block [&_h3]:text-[16px] [&_h3]:font-semibold [&_h3]:tracking-[-0.16px] [&_h4]:block [&_h4]:text-[16px] [&_h4]:font-semibold [&_h4]:tracking-[-0.16px] [&_h5]:block [&_h5]:text-[14px] [&_h5]:font-semibold [&_h5]:tracking-[-0.14px] [&_h6]:block [&_h6]:text-[12px] [&_h6]:font-semibold [&_h6]:tracking-[-0.12px] [&_p]:text-[16px] [&_p]:tracking-[-0.16px] [&_p]:leading-[150%] [&_p]:mb-2 [&_p:last-child]:mb-0 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-4 [&_blockquote]:pl-3 [&_blockquote]:my-2 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_blockquote+blockquote]:-mt-2 [&_blockquote+blockquote]:pt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_a]:underline p-4';

/**
 * Maps MDXEditor's Radix slate palette onto our gray tokens, on the editor
 * root and on its popups (block type menu, link dialog), which set the same
 * variables. Every hover, pressed and disabled state reads these.
 */
const MARKDOWN_EDITOR_THEME_CLASS =
  '[&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseBase:var(--color-gray-1)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseBgSubtle:var(--color-gray-1)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseBg:var(--color-gray-2)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseBgHover:var(--color-gray-2)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseBgActive:var(--color-gray-3)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseLine:var(--color-gray-4)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseBorder:var(--color-gray-4)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseBorderHover:var(--color-gray-5)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseText:var(--color-secondary-text)] [&_:is(.mdxeditor,.mdxeditor-popup-container)]:[--baseTextContrast:var(--color-primary-text)]';

/**
 * A flat white bar with a divider above the content. Icons are muted and turn
 * dark on hover or when pressed: MDXEditor colors them with
 * `--baseTextContrast`, so redefining it per button recolors the icon inside.
 * Radius is `!` because MDXEditor joins grouped toggles (bold/italic/underline)
 * with more specific corner rules.
 */
const MARKDOWN_EDITOR_TOOLBAR_CLASS =
  '[&_.mdxeditor-toolbar]:rounded-none [&_.mdxeditor-toolbar]:bg-white [&_.mdxeditor-toolbar]:border-b [&_.mdxeditor-toolbar]:border-gray-4 [&_.mdxeditor-toolbar]:px-2 [&_.mdxeditor-toolbar]:gap-0.5 [&_.mdxeditor-toolbar]:[--baseTextContrast:var(--color-secondary-text)] [&_.mdxeditor-toolbar_button]:!rounded-md [&_.mdxeditor-toolbar_button]:p-1 [&_.mdxeditor-toolbar_button]:transition-colors [&_.mdxeditor-toolbar_button:hover]:bg-gray-2 [&_.mdxeditor-toolbar_button:hover]:[--baseTextContrast:var(--color-primary-text)] [&_.mdxeditor-toolbar_button[data-state=on]]:!bg-gray-3 [&_.mdxeditor-toolbar_button[data-state=on]]:[--baseTextContrast:var(--color-primary-text)] [&_.mdxeditor-toolbar_[role=separator]]:mx-1 [&_.mdxeditor-toolbar_[role=separator]]:border-r-0 [&_.mdxeditor-toolbar_button[role=combobox]]:bg-transparent [&_.mdxeditor-toolbar_button[role=combobox]]:text-primary-text [&_.mdxeditor-toolbar_button[role=combobox]]:px-2 [&_.mdxeditor-toolbar_button[role=combobox][data-state=open]]:bg-gray-2';

export interface MarkdownEditorProps {
  markdown: string;
  /**
   * `initialMarkdownNormalize` is true when the editor rewrote the markdown it
   * was given (spacing, bullet symbols, links made of bare URLs and emails…)
   * rather than the user editing it.
   */
  onChange: (markdown: string, initialMarkdownNormalize: boolean) => void;
  placeholder?: string;
  readOnly?: boolean;
  /** Registered after the standard plugins (e.g. @-mention typeahead). */
  extraPlugins?: RealmPlugin[];
  /** Wrapper box classes (border, height, overflow). */
  className?: string;
  /** Appended to {@link MARKDOWN_EDITOR_CONTENT_CLASS}. */
  contentEditableClassName?: string;
}

/**
 * Standard markdown editor built on MDXEditor with the shared toolbar and
 * prose styling.
 *
 * Numbered markdown lists are omitted from the toolbar and from typing
 * shortcuts: `1.` / `2.` stay as paragraphs instead of nesting. Use bullets
 * for true lists.
 */
const MarkdownEditor = forwardRef<MDXEditorMethods, MarkdownEditorProps>(
  (
    {
      markdown,
      onChange,
      placeholder,
      readOnly,
      extraPlugins,
      className,
      contentEditableClassName
    },
    ref
  ) => {
    const editorRef = useRef<MDXEditorMethods>(null);
    // MDXEditor only flags its first rewrite of the loaded markdown. The link
    // plugin then turns bare emails and URLs into links in a later update,
    // which it reports as an edit. Every edit starts with focusing the editor
    // or its toolbar, so a change before then is still the editor's own.
    const hasFocusedRef = useRef(false);
    // MDXEditor portals its popups (block type select, link dialog) to
    // document.body by default. Inside a HeroUI modal or drawer, the
    // react-aria focus trap pulls focus back out of them and the two
    // loop until the page throws, so the popups never open. Rendering
    // them next to the editor keeps them inside whatever traps focus.
    const [overlayContainer, setOverlayContainer] =
      useState<HTMLDivElement | null>(null);
    const paragraphMarkdown = useMemo(
      () => escapeOrderedListMarkers(markdown),
      [markdown]
    );

    useImperativeHandle(
      ref,
      () => ({
        getMarkdown: () => editorRef.current?.getMarkdown() ?? '',
        setMarkdown: (value: string) => {
          editorRef.current?.setMarkdown(escapeOrderedListMarkers(value));
        },
        insertMarkdown: (value: string) => {
          editorRef.current?.insertMarkdown(value);
        },
        focus: (callbackFn, opts) => {
          editorRef.current?.focus(callbackFn, opts);
        },
        getContentEditableHTML: () =>
          editorRef.current?.getContentEditableHTML() ?? '',
        getSelectionMarkdown: () =>
          editorRef.current?.getSelectionMarkdown() ?? ''
      }),
      []
    );

    const plugins = useMemo(
      () => [
        headingsPlugin(),
        listsPlugin(),
        quotePlugin(),
        quoteParagraphsPlugin(),
        thematicBreakPlugin(),
        linkPlugin(),
        markdownShortcutWithoutNumberedListsPlugin(),
        tablePlugin(),
        linkDialogPlugin(),
        // No toolbar button inserts images here (they're managed through the
        // attachment/image-gallery flow), but MDXEditor still needs this
        // plugin registered to parse `![]()` markdown already in the
        // content — without it, any embedded image fails the whole
        // document's parse and the editor renders empty.
        imagePlugin(),
        toolbarPlugin({
          toolbarContents: () => (
            <>
              <UndoRedo />
              <BoldItalicUnderlineToggles />
              <BlockTypeSelect />
              <CreateLink />
              <InsertTable />
              <ListsToggle options={['bullet']} />
            </>
          )
        }),
        ...(extraPlugins ?? [])
      ],
      [extraPlugins]
    );

    return (
      <div
        className={cn(
          '[&_[class*="placeholder"]]:!text-gray-4',
          MARKDOWN_EDITOR_THEME_CLASS,
          MARKDOWN_EDITOR_TOOLBAR_CLASS,
          className
        )}
        onFocusCapture={() => {
          hasFocusedRef.current = true;
        }}
      >
        <MDXEditor
          ref={editorRef}
          markdown={paragraphMarkdown}
          onChange={(value, initialMarkdownNormalize) =>
            onChange(value, initialMarkdownNormalize || !hasFocusedRef.current)
          }
          placeholder={placeholder}
          readOnly={readOnly}
          onError={({ error, source }) => {
            console.error('MDXEditor parse error', { error, source });
          }}
          suppressHtmlProcessing
          plugins={plugins}
          overlayContainer={overlayContainer}
          contentEditableClassName={cn(
            MARKDOWN_EDITOR_CONTENT_CLASS,
            contentEditableClassName
          )}
        />
        {/* Stacks the popups above siblings the host layers over the editor
            (e.g. a scroll fade above a pinned reply box). */}
        <div ref={setOverlayContainer} className="relative z-50" />
      </div>
    );
  }
);

MarkdownEditor.displayName = 'MarkdownEditor';

export default MarkdownEditor;
